import { createRouter } from "@tanstack/react-router";
import { DefaultCatchBoundary } from "~/components/default-catch-boundary";
import { NotFound } from "~/components/not-found";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { routerWithDbClient } from "@tanstack/react-router-with-db";
import { getAppDbClient, getAppQueryClient } from "~/lib/ssr/runtime";
import { shouldDehydrateSsrQuery } from "~/lib/ssr/query-dehydration";
import { resolvePageViewTransitionTypes } from "~/lib/page-view-transitions";
import { localeUrlRewrite } from "~/lib/language-lens";
import { createRouterRuntimeContext } from "~/lib/router-runtime";
import { routeTree } from "~/routeTree.gen";

export function getRouter() {
  const queryClient = getAppQueryClient();
  const dbClient = getAppDbClient();
  const router = createRouter({
    routeTree,
    rewrite: localeUrlRewrite,
    context: { ...createRouterRuntimeContext({ queryClient }), dbClient },
    defaultPreload: "intent",
    // Route loaders delegate server-data freshness and dedupe to TanStack Query.
    // A zero router window prevents a second cache policy from masking Query's.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultViewTransition: {
      types: resolvePageViewTransitionTypes,
    },
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFound />,
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    dehydrateOptions: { shouldDehydrateQuery: shouldDehydrateSsrQuery },
  });
  return routerWithDbClient(router, dbClient);
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
